import { useState, useEffect } from "react";
import Modal from "./Modal";
import CloseBg from "../assets/images/bg_close.svg";
import styled from "styled-components";
import { useCustomPlayerModal } from "../contexts/ModalContext";
import PlayerCard from "./PlayerCard";
import { teamKeys, yearKeys } from "../utils/publicKeys";
import { useSquad } from "../contexts/SquadContext";
import { usePlayerStore } from "../contexts/PlayerContext";
import { calculateSynergyStates } from "../utils/calculateSynergyStates";

const ModalContainer = styled.div`
    width: 84rem;
    height: 54rem;
    background-color: white;
`;

const ModalHeader = styled.div`
    position: relative;
    height: 5.75rem;
    justify-content: center;
    align-items: center;
    display: flex;
    font-family: 'Esamanru', sans-serif;
    font-size: 1.5rem;
    font-weight: 200;
    color: #545454;
    border-bottom: 0.125rem solid #545454;
`

const CloseButton = styled.img`
    position: absolute;
    right: 1.625rem;
    width: 2.5rem;
    height: 2.5rem;
    cursor: pointer;
`

const ModalContent = styled.div`
    display: flex;
    flex-direction: row;
    height: calc(100% - 5.75rem);
`

const LeftSection = styled.div`
    flex: 1;
    padding: 2rem;
    border-right: 0.125rem solid #e0e0e0;
    overflow-y: auto;
`

const RightSection = styled.div`
    flex: 1;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 2rem;
`

const PlayerCardWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 22rem;
    height: 32rem;
`

const CreateButton = styled.button`
    width: 23rem;
    padding: 1.25rem 0;
    background-color: #A939FF;
    color: white;
    border: none;
    border-radius: 0.5rem;
    font-family: 'Esamanru', sans-serif;
    font-size: 1.5rem;
    font-weight: 500;
    cursor: pointer;
    &:hover {
        background-color: #9929EF;
    }
    &:active {
        background-color: #9929EF;
    }
`

const FormSection = styled.div`
    margin-bottom: 1.5rem;
`

const FormRow = styled.div`
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
`

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    flex: ${props => props.$flex || 1};
    min-width: 0;
`

const Label = styled.label`
    font-family: 'Pretendard Variable';
    font-size: 0.875rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
`

const Input = styled.input`
    padding: 1.25rem 1.5rem;
    border: 0.0625rem solid #ddd;
    border-radius: 0.5rem;
    font-family: 'Pretendard Variable';
    font-size: 1.325rem;
    &:focus {
        outline: none;
        border-color: #A939FF;
    }
    &:disabled {
        background-color: #f5f5f5;
        color: #999;
    }
    &[type="number"] {
        -moz-appearance: textfield;
    }
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
`

const Select = styled.select`
    padding: 1.25rem 3rem 1.25rem 1.5rem;
    border: 0.0625rem solid #ddd;
    border-radius: 0.5rem;
    font-family: 'Pretendard Variable';
    font-size: 1.325rem;
    background-color: white;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 1rem center;
    background-size: 1.5rem;
    cursor: pointer;
    &:focus {
        outline: none;
        border-color: #A939FF;
    }
    &:disabled {
        background-color: #f5f5f5;
        color: #999;
        cursor: not-allowed;
    }
`

const SectionTitle = styled.h3`
    font-family: 'Esamanru', sans-serif;
    font-size: 1.125rem;
    font-weight: 500;
    color: #333;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 0.125rem solid #A939FF;
`

const StatGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
`

const NumberInputWrapper = styled.div`
    display: flex;
    align-items: stretch;
    border: 0.0625rem solid #ddd;
    border-radius: 0.5rem;
    overflow: hidden;
    width: 100%;
    &:focus-within {
        border-color: #A939FF;
    }
`

const NumberInputField = styled.input`
    flex: 1;
    padding: 1.25rem 0.5rem;
    border: none;
    font-family: 'Pretendard Variable';
    font-size: 1.325rem;
    text-align: center;
    min-width: 0;
    &:focus {
        outline: none;
    }
    &:disabled {
        background-color: #f5f5f5;
        color: #999;
    }
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
`

const NumberButton = styled.button`
    width: 2.5rem;
    border: none;
    background-color: #f5f5f5;
    font-size: 1.5rem;
    font-weight: 600;
    color: #333;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
        background-color: #e0e0e0;
    }
    &:active {
        background-color: #d0d0d0;
    }
    &:disabled {
        color: #999;
        cursor: not-allowed;
    }
`

// 숫자 입력 컴포넌트
function NumberInput({ value, onChange, min = 0, max = 999, disabled = false }) {
    const [inputValue, setInputValue] = useState(String(value));

    useEffect(() => {
        setInputValue(String(value));
    }, [value]);

    const handleChange = (e) => {
        const rawValue = e.target.value;
        setInputValue(rawValue);

        if (rawValue === '') return;

        const numValue = parseInt(rawValue, 10);
        if (!isNaN(numValue)) {
            onChange(Math.max(min, Math.min(max, numValue)));
        }
    };

    const handleBlur = () => {
        if (inputValue === '' || isNaN(parseInt(inputValue, 10))) {
            setInputValue(String(min));
            onChange(min);
        } else {
            const numValue = Math.max(min, Math.min(max, parseInt(inputValue, 10)));
            setInputValue(String(numValue));
            onChange(numValue);
        }
    };

    const increment = () => {
        if (value < max) onChange(value + 1);
    };

    const decrement = () => {
        if (value > min) onChange(value - 1);
    };

    return (
        <NumberInputWrapper>
            <NumberButton onClick={decrement} disabled={disabled || value <= min}>-</NumberButton>
            <NumberInputField
                type="text"
                inputMode="numeric"
                value={inputValue}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={disabled}
            />
            <NumberButton onClick={increment} disabled={disabled || value >= max}>+</NumberButton>
        </NumberInputWrapper>
    );
}

// 카드 종류 목록
const cardTypes = ['시그니쳐', '임팩트', '국가대표', '골든글러브', '라이브', '올스타', '시즌'];

// 포지션 목록
const positionOptions = ['LF', 'CF', 'RF', 'SS', '2B', '1B', '3B', 'C', 'DH', 'SP', 'RP', 'CP'];

// 타자 포지션
const batterPositions = ['LF', 'CF', 'RF', 'SS', '2B', '1B', '3B', 'C', 'DH'];

// 타자 스탯 라벨
const batterStatLabels = ['파워', '정확', '선구', '인내', '주루', '수비'];

// 투수 스탯 라벨
const pitcherStatLabels = ['구속', '변화', '구위', '제구', '지구력', '수비'];

// 라이브 description 옵션
const liveDescriptionOptions = ['LIVE V1', 'LIVE V2', 'LIVE V3'];

// 별 갯수 규칙
const getStarOptions = (cardType) => {
    switch (cardType) {
        case '시그니쳐': return [5];
        case '임팩트': return [4];
        case '국가대표': return [5];
        case '골든글러브': return [4, 5];
        case '라이브': return [1, 2, 3, 4, 5];
        case '올스타': return [5];
        case '시즌': return [3, 4, 5];
        default: return [5];
    }
};

// 세트덱 스코어 규칙
const getSetDeckScore = (cardType, year) => {
    switch (cardType) {
        case '시그니쳐': return { fixed: true, value: 5 };
        case '임팩트': return { fixed: true, value: 4 };
        case '국가대표': return { fixed: true, value: 4 };
        case '시즌': return { fixed: true, value: 4 };
        case '골든글러브': return { fixed: true, value: 3 };
        case '올스타': return { fixed: true, value: year === '25' ? 4 : 2 };
        case '라이브': return { fixed: false, min: 1, max: 13 };
        default: return { fixed: true, value: 4 };
    }
};

// 연도 필요 여부
const needsYear = (cardType) => {
    return ['시그니쳐', '국가대표', '골든글러브', '올스타', '시즌', '라이브'].includes(cardType);
};

// Description 타입
const getDescriptionType = (cardType) => {
    if (cardType === '임팩트') return 'text';
    if (cardType === '라이브') return 'select';
    return 'none';
};

function checkPositionAvailable(position, player) {
    if (['SUB1', 'SUB2', 'SUB3', 'SUB4', 'SUB5', 'SUB6', 'DH'].includes(position)) {
        return ['LF', 'CF', 'RF', '1B', '2B', 'SS', '3B', 'C', 'DH'].includes(player.position);
    } else if (['SP1', 'SP2', 'SP3', 'SP4', 'SP5'].includes(position)) {
        return (player.position === 'SP');
    } else if (['RP1', 'RP2', 'RP3', 'RP4', 'RP5', 'RP6'].includes(position)) {
        return (player.position === 'RP');
    } else {
        return player.position === position;
    }
}

function checkDuplicatedPlayerInDatabase(newPlayer, playersData) {
    const normalizeValue = (value) => value === null || value === '' ? null : value;

    return playersData.some(player =>
        player.playerName === newPlayer.playerName &&
        player.year === newPlayer.year &&
        player.type === newPlayer.type &&
        normalizeValue(player.description) === normalizeValue(newPlayer.description)
    );
}

function checkDuplicatedPlayerInSquad(newPlayer, playerInfos) {
    const playerPositions = [
        'LF', 'CF', 'RF', '1B', '2B', '3B', 'SS', 'C', 'DH', 'SUB1', 'SUB2', 'SUB3', 'SUB4', 'SUB5', 'SUB6',
        'SP1', 'SP2', 'SP3', 'SP4', 'SP5', 'RP1', 'RP2', 'RP3', 'RP4', 'RP5', 'RP6', 'CP'
    ];

    return playerPositions.some((key) => {
        return playerInfos[key] && playerInfos[key].playerName === newPlayer.playerName;
    });
}

export default function CustomPlayerModal() {
    const { isCustomPlayerModalOpen, closeCustomPlayerModal } = useCustomPlayerModal();
    const { playerInfos, addPlayer } = useSquad();
    const { playersData, setPlayersData } = usePlayerStore();

    // 폼 상태
    const [playerName, setPlayerName] = useState('홍길동');
    const [cardType, setCardType] = useState('시즌');
    const [stars, setStars] = useState(5);
    const [position, setPosition] = useState('SS');
    const [team, setTeam] = useState('두산');
    const [year, setYear] = useState('24');
    const [setDeckScore, setSetDeckScore] = useState(4);
    const [description, setDescription] = useState('');
    const [stats, setStats] = useState({
        s1: 70, s2: 70, s3: 70, s4: 70, s5: 70, s6: 70
    });

    const isPitcher = !batterPositions.includes(position);
    const statLabels = isPitcher ? pitcherStatLabels : batterStatLabels;

    // 카드 종류 변경 시 관련 값 업데이트
    useEffect(() => {
        const starOptions = getStarOptions(cardType);
        if (!starOptions.includes(stars)) {
            setStars(starOptions[starOptions.length - 1]);
        }

        const deckScore = getSetDeckScore(cardType, year);
        if (deckScore.fixed) {
            setSetDeckScore(deckScore.value);
        }

        const descType = getDescriptionType(cardType);
        if (descType === 'none') {
            setDescription('');
        } else if (descType === 'text') {
            setDescription('가을사나이');
        } else if (descType === 'select' && !liveDescriptionOptions.includes(description)) {
            setDescription(liveDescriptionOptions[0]);
        }
    }, [cardType, year]);

    // 연도 변경 시 올스타 세트덱 스코어 업데이트
    useEffect(() => {
        if (cardType === '올스타') {
            setSetDeckScore(year === '25' ? 4 : 2);
        }
    }, [year, cardType]);

    const handleStatChange = (key, value) => {
        setStats(prev => ({ ...prev, [key]: value }));
    };

    function makePlayerFull(player, position) {
        const synergyStates = calculateSynergyStates(player, {...playerInfos, [position]: player}, position);

        return {
            ...player,
            level: 0,
            isAwaken: false,
            isFa: null,
            trainedStates: { level: 0, s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
            specialTrainedStates: { level: 0, s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
            positionTrainedStates: { level: 0, s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
            positionTrainedAdditionalStates: {s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
            enforcedStates: {s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
            awakenStates: {s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
            setDeckStates: {s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
            synergyStates: synergyStates,
            faStates: {s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
        }
    }

    const handleCreatePlayer = () => {
        // 유효성 검사
        if (!playerName.trim()) {
            alert("선수 이름을 입력해주세요.");
            return;
        }

        if (!cardType) {
            alert("카드 종류를 선택해주세요.");
            return;
        }

        if (!team) {
            alert("구단을 선택해주세요.");
            return;
        }

        if (!position) {
            alert("포지션을 선택해주세요.");
            return;
        }

        if (!stars) {
            alert("별 갯수를 선택해주세요.");
            return;
        }

        if (needsYear(cardType) && !year) {
            alert("연도를 선택해주세요.");
            return;
        }

        const descriptionType = getDescriptionType(cardType);
        if (descriptionType === 'text' && !description.trim()) {
            alert("임팩트 종류를 입력해주세요.");
            return;
        }

        if (descriptionType === 'select' && !description) {
            alert("라이브 버전을 선택해주세요.");
            return;
        }

        if (setDeckScore === null || setDeckScore === '') {
            alert("세트덱 스코어를 입력해주세요.");
            return;
        }

        const basePlayer = {
            id: `custom-${Date.now()}`,
            originalOvr: Math.floor((stats.s1 + stats.s2 + stats.s3 + stats.s4 + stats.s5 + stats.s6) / 6),
            year: needsYear(cardType) ? year : null,
            stars: stars,
            playerName: playerName,
            description: description,
            team: team,
            position: position,
            type: cardType,
            isPitcher: isPitcher,
            setDeckScore: setDeckScore,
            originalStates: {
                level: 0,
                s1: stats.s1,
                s2: stats.s2,
                s3: stats.s3,
                s4: stats.s4,
                s5: stats.s5,
                s6: stats.s6
            }
        };

        // 데이터베이스에서 중복 확인
        const isDuplicatedInDatabase = checkDuplicatedPlayerInDatabase(basePlayer, playersData);
        if (isDuplicatedInDatabase) {
            alert("동일한 선수(이름, 연도, 종류, 설명)가 이미 존재합니다.");
            return;
        }

        // 포지션 확인
        const isPositionAvailable = checkPositionAvailable(isCustomPlayerModalOpen, basePlayer);

        // 스쿼드에서 중복 확인
        const isDuplicatedInSquad = checkDuplicatedPlayerInSquad(basePlayer, playerInfos);

        // PlayerProvider의 전체 데이터에 추가 (항상 추가)
        setPlayersData([...playersData, basePlayer]);

        // 포지션과 스쿼드 중복 모두 확인
        if (isPositionAvailable && !isDuplicatedInSquad) {
            // 선수 생성
            const fullPlayer = makePlayerFull(basePlayer, isCustomPlayerModalOpen);
            // Squad에 추가
            addPlayer(isCustomPlayerModalOpen, fullPlayer);
            alert("선수가 생성되어 데이터베이스에 추가되었습니다.\n\n(주의: 새로고침 시 추가된 선수는 사라집니다.)");
            closeCustomPlayerModal();
        } else {
            // 데이터베이스에만 추가되고 스쿼드에는 추가되지 않음
            if (!isPositionAvailable) {
                alert("포지션이 맞지 않아 스쿼드에 추가되지 않았습니다.\n데이터베이스에는 추가되었습니다.\n검색하여 사용할 수 있습니다.\n\n(주의: 새로고침 시 추가된 선수는 사라집니다.)");
            } else if (isDuplicatedInSquad) {
                alert("동일한 이름의 선수가 이미 스쿼드에 있어 추가되지 않았습니다.\n데이터베이스에는 추가되었습니다.\n검색하여 사용할 수 있습니다.\n\n(주의: 새로고침 시 추가된 선수는 사라집니다.)");
            }
            closeCustomPlayerModal();
        }
    };

    // 미리보기용 플레이어 객체
    const previewPlayer = {
        id: 'custom-preview',
        originalOvr: Math.floor((stats.s1 + stats.s2 + stats.s3 + stats.s4 + stats.s5 + stats.s6) / 6),
        year: needsYear(cardType) ? year : null,
        stars: stars,
        playerName: playerName,
        description: description,
        team: team,
        position: position,
        type: cardType,
        isPitcher: isPitcher,
        level: 0,
        isAwaken: false,
        setDeckScore: setDeckScore,
    };

    const starOptions = getStarOptions(cardType);
    const deckScoreRule = getSetDeckScore(cardType, year);
    const descriptionType = getDescriptionType(cardType);

    return (
        <Modal
            isOpen={isCustomPlayerModalOpen}
            onClose={closeCustomPlayerModal}
        >
            <ModalContainer>
                <ModalHeader>
                    커스텀 선수 생성 - {isCustomPlayerModalOpen}
                    <CloseButton
                        src={CloseBg}
                        onClick={closeCustomPlayerModal}
                    />
                </ModalHeader>

                <ModalContent>
                    <LeftSection>
                        <FormSection>
                            <SectionTitle>기본 정보</SectionTitle>
                            <FormRow>
                                <FormGroup $flex={2}>
                                    <Label>선수 이름</Label>
                                    <Input
                                        type="text"
                                        value={playerName}
                                        onChange={(e) => {
                                            let value = e.target.value.slice(0, 10);
                                            // 특수기호만 제거
                                            value = value.replace(/[`!@#$%^&*()\-=\[\]{};':"\\|,.<>\/?]/g, '');
                                            setPlayerName(value);
                                        }}
                                        maxLength={10}
                                        placeholder="선수 이름 (최대 10글자)"
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>카드 종류</Label>
                                    <Select
                                        value={cardType}
                                        onChange={(e) => setCardType(e.target.value)}
                                    >
                                        {cardTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </Select>
                                </FormGroup>
                            </FormRow>

                            <FormRow>
                                <FormGroup>
                                    <Label>구단</Label>
                                    <Select
                                        value={team}
                                        onChange={(e) => setTeam(e.target.value)}
                                    >
                                        {teamKeys.map(t => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </Select>
                                </FormGroup>
                                <FormGroup>
                                    <Label>포지션</Label>
                                    <Select
                                        value={position}
                                        onChange={(e) => setPosition(e.target.value)}
                                    >
                                        {positionOptions.map(pos => (
                                            <option key={pos} value={pos}>{pos}</option>
                                        ))}
                                    </Select>
                                </FormGroup>
                                <FormGroup>
                                    <Label>별 갯수</Label>
                                    <Select
                                        value={stars}
                                        onChange={(e) => setStars(parseInt(e.target.value))}
                                        disabled={starOptions.length === 1}
                                    >
                                        {starOptions.map(s => (
                                            <option key={s} value={s}>{s}성</option>
                                        ))}
                                    </Select>
                                </FormGroup>
                            </FormRow>

                            <FormRow>
                                {needsYear(cardType) && (
                                    <FormGroup>
                                        <Label>연도</Label>
                                        <Select
                                            value={year}
                                            onChange={(e) => setYear(e.target.value)}
                                        >
                                            {yearKeys.map(y => (
                                                <option key={y} value={y}>{y}</option>
                                            ))}
                                        </Select>
                                    </FormGroup>
                                )}
                                <FormGroup>
                                    <Label>세트덱 스코어</Label>
                                    <NumberInput
                                                                                value={setDeckScore}
                                        onChange={setSetDeckScore}
                                        min={deckScoreRule.min || 1}
                                        max={deckScoreRule.max || 13}
                                        disabled={deckScoreRule.fixed}
                                    />
                                </FormGroup>
                                {descriptionType === 'text' && (
                                    <FormGroup $flex={2}>
                                        <Label>임팩트 종류</Label>
                                        <Input
                                            type="text"
                                            value={description}
                                            onChange={(e) => {
                                                let value = e.target.value.slice(0, 30);
                                                // 특수기호만 제거
                                                value = value.replace(/[`!@#$%^&*()\-=\[\]{};':"\\|,.<>\/?]/g, '');
                                                setDescription(value);
                                            }}
                                            maxLength={30}
                                            placeholder="임팩트 종류 입력 (최대 30글자)"
                                        />
                                    </FormGroup>
                                )}
                                {descriptionType === 'select' && (
                                    <FormGroup>
                                        <Label>라이브 버전</Label>
                                        <Select
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                        >
                                            {liveDescriptionOptions.map(opt => (
                                                <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                        </Select>
                                    </FormGroup>
                                )}
                            </FormRow>
                        </FormSection>

                        <FormSection>
                            <SectionTitle>기본 스탯 {isPitcher ? '(투수)' : '(타자)'}</SectionTitle>
                            <StatGrid>
                                {['s1', 's2', 's3', 's4', 's5', 's6'].map((key, index) => (
                                    <FormGroup key={key}>
                                        <Label>{statLabels[index]}</Label>
                                        <NumberInput
                                                                                        value={stats[key]}
                                            onChange={(val) => handleStatChange(key, val)}
                                            min={0}
                                            max={999}
                                        />
                                    </FormGroup>
                                ))}
                            </StatGrid>
                        </FormSection>
                    </LeftSection>

                    <RightSection>
                        <PlayerCardWrapper>
                            <PlayerCard
                                key={`${playerName}-${team}`}
                                player={previewPlayer}
                                scale={3.5}
                                isForDisplay={true}
                            />
                        </PlayerCardWrapper>
                        <CreateButton onClick={handleCreatePlayer}>
                            생성
                        </CreateButton>
                    </RightSection>
                </ModalContent>

            </ModalContainer>
        </Modal>
    )
}
